/* VikingsBrand Rune Cast, the draw engine behind /pages/rune-reading.
   Deliberately a separate file from vb-runes.js: that one powers the rune
   translator, which is the only rune asset on the store currently earning
   clicks, and this tool has no business being able to break it.

   Everything is client-side. Nothing typed here is transmitted or stored.

   TWO DATA FILES, deliberately separate:
   - rune-poems-dataset.json  names, meanings and the three medieval rune poems.
     Attested, published by us under CC BY 4.0. Nothing in it is invented here.
   - vb-rune-readings.json    the reading texts. OURS, modern, all rights
     reserved. Per rune and orientation: a two-sentence `body`, a three-sentence
     standalone `means`, and five position openers.

   HONESTY RULES, load-bearing (see SEO/RUNE-READING-PLAN.md §0.3):
   the reading texts are reflection prompts drawn from what the poems say, not
   historical divinatory meanings, no such record survives. Reversed reading is
   a 20th-century convention. The page says both out loud. */
(function () {
  'use strict';
  if (window.VBRuneCast) { return; } /* idempotent: never double-boot */

  var DATA = null;      /* rune-poems-dataset.json */
  var READ = null;      /* vb-rune-readings.json  */
  var STONES = {};      /* slug -> stone image URL, injected by the section */

  /* ---------- per-rune index ----------
     KEYED ON THE DATASET'S OWN `elder_futhark_name`, which is not always the
     name people search for: the dataset carries the reconstructed Proto-Germanic
     forms Naudiz, Isaz and Berkanan, while our own 24 spoke articles, and the
     queries in Search Console, use Nauthiz, Isa and Berkano. Deriving either the
     label or the article slug from the dataset string produces a wrong name and a
     404, so both are declared here.

     `sym` marks a stave unchanged by a 180-degree rotation. Reversal only means
     anything if the shape actually changes when you turn it, so those ten runes
     are never marked reversed. Checkable by eye, which is why this defers to the
     geometry rather than to any one modern list.
     `noun`, `att` and `tone` feed the synthesis; see synthesize(). */
  var RUNES = {
    Fehu:     { slug: 'fehu',     noun: 'wealth',              att: 1, tone: 'mixed'  },
    Uruz:     { slug: 'uruz',     noun: 'wild strength',       att: 1, tone: 'hard'   },
    Thurisaz: { slug: 'thurisaz', noun: 'a thorn',             att: 1, tone: 'hard'   },
    Ansuz:    { slug: 'ansuz',    noun: 'speech',              att: 1, tone: 'bright' },
    Raido:    { slug: 'raido',    noun: 'a journey',           att: 1, tone: 'mixed'  },
    Kenaz:    { slug: 'kenaz',    noun: 'a torch',             att: 1, tone: 'mixed'  },
    Gebo:     { slug: 'gebo',     noun: 'a gift',              att: 1, tone: 'bright', sym: true },
    Wunjo:    { slug: 'wunjo',    noun: 'joy',                 att: 1, tone: 'bright' },
    Hagalaz:  { slug: 'hagalaz',  noun: 'hail out of nowhere', att: 2, tone: 'hard',   sym: true },
    Naudiz:   { slug: 'nauthiz',  noun: 'need',                att: 2, tone: 'hard',   sym: true, label: 'Nauthiz' },
    Isaz:     { slug: 'isa',      noun: 'ice',                 att: 2, tone: 'hard',   sym: true, label: 'Isa' },
    Jera:     { slug: 'jera',     noun: 'the harvest',         att: 2, tone: 'bright', sym: true },
    Eihwaz:   { slug: 'eihwaz',   noun: 'endurance',           att: 2, tone: 'mixed',  sym: true },
    Perthro:  { slug: 'perthro',  noun: 'the unknown',         att: 2, tone: 'mixed'  },
    Algiz:    { slug: 'algiz',    noun: 'protection',          att: 2, tone: 'mixed'  },
    Sowilo:   { slug: 'sowilo',   noun: 'the sun',             att: 2, tone: 'bright', sym: true },
    Tiwaz:    { slug: 'tiwaz',    noun: 'a price paid',        att: 3, tone: 'mixed'  },
    Berkanan: { slug: 'berkano',  noun: 'new growth',          att: 3, tone: 'bright', label: 'Berkano' },
    Ehwaz:    { slug: 'ehwaz',    noun: 'partnership',         att: 3, tone: 'bright' },
    Mannaz:   { slug: 'mannaz',   noun: 'other people',        att: 3, tone: 'mixed',  sym: true },
    Laguz:    { slug: 'laguz',    noun: 'deep water',          att: 3, tone: 'mixed'  },
    Ingwaz:   { slug: 'ingwaz',   noun: 'something finished',  att: 3, tone: 'bright', sym: true },
    Othala:   { slug: 'othala',   noun: 'inheritance',         att: 3, tone: 'bright' },
    Dagaz:    { slug: 'dagaz',    noun: 'daybreak',            att: 3, tone: 'bright', sym: true }
  };
  function entry(rec) { return RUNES[rec.elder_futhark_name] || {}; }

  /* ---------- cast shapes ----------
     `key` selects the position opener from vb-rune-readings.json; `label` is what
     the card prints. The single draw uses the `present` opener, a daily rune or a
     yes-no question is asked about now. */
  var MODES = {
    single: { id: 'single', label: 'One rune',
              positions: [{ key: 'present', label: 'Your rune' }],
              cardTitle: 'One rune',
              note: 'One stave, for a daily draw or a plain yes-or-no question.' },
    three:  { id: 'three', label: 'Three runes',
              positions: [{ key: 'past', label: 'Past' },
                          { key: 'present', label: 'Present' },
                          { key: 'future', label: 'Future' }],
              cardTitle: 'Past, present and future',
              note: 'The familiar past, present and future cast, invented in the 20th century. No Viking-age rune spread survives, so there is no older layout this one replaced.' },
    norn:   { id: 'norn', label: 'Five runes',
              positions: [{ key: 'past',    label: 'Urðr, what was laid down' },
                          { key: 'present', label: 'Verðandi, what is being made' },
                          { key: 'future',  label: 'Skuld, what is owed' },
                          { key: 'thorn',   label: 'The thorn, what is in the way' },
                          { key: 'stave',   label: 'The stave, what holds it up' }],
              cardTitle: 'The Norn spread',
              note: 'Ours, named for the three Norns who shape fate at the well beneath Yggdrasil, with two staves added for what blocks the matter and what holds it up. The Norns are attested. The five-position layout is not.' }
  };

  /* ---------- seeded randomness ----------
     A cast has to be reproducible or the permalink is a lie: open someone's
     shared link and you must see the draw they saw, not a fresh one. */
  function fnv1a(str) {
    var h = 2166136261 >>> 0;
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
    return h >>> 0;
  }
  function mulberry32(a) {
    return function () {
      a |= 0; a = a + 0x6D2B79F5 | 0;
      var t = Math.imul(a ^ a >>> 15, 1 | a);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }
  function newSeed() { return Math.floor(Math.random() * 0xFFFFFFFF).toString(36); }
  /* The daily draw is the same rune all day for a visitor and a different one
     tomorrow. Local date, so it turns over at the reader's own midnight. */
  function daySeed() {
    var d = new Date();
    return 'day-' + d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
  }

  /* ---------- the draw ---------- */
  function draw(opts) {
    if (!DATA || !DATA.runes || !DATA.runes.length) { return null; }
    opts = opts || {};
    var mode = MODES[opts.mode] ? opts.mode : 'single';
    var m = MODES[mode];
    var seed = opts.seed || newSeed();
    var allowRev = opts.reversed !== false;
    var rnd = mulberry32(fnv1a(seed + '|' + mode + '|' + (allowRev ? 'r' : 'u')));

    /* draw without replacement: a rune cannot land twice in one cast */
    var pool = [];
    for (var i = 0; i < DATA.runes.length; i++) { pool.push(i); }
    var picks = [];
    for (var p = 0; p < m.positions.length && pool.length; p++) {
      var k = Math.floor(rnd() * pool.length);
      var idx = pool.splice(k, 1)[0];
      var canRev = !entry(DATA.runes[idx]).sym;
      picks.push({
        idx: idx,
        reversed: allowRev && canRev && rnd() < 0.5,
        canReverse: canRev,
        position: m.positions[p].label,
        positionKey: m.positions[p].key
      });
    }
    return { seed: seed, mode: mode, reversed: allowRev, picks: picks };
  }

  /* Merge the dataset record, the reading text and the stone image into one card.
     `reading` is the position opener followed by the rune's body, the opener is
     what makes the same rune read differently in Past than it does in Future. */
  function card(pick) {
    var r = DATA.runes[pick.idx];
    var e = entry(r);
    var name = e.label || r.elder_futhark_name;
    var t = (READ && READ.runes && READ.runes[r.elder_futhark_name]) || {};
    var o = t[pick.reversed ? 'rev' : 'up'] || t.up || {};
    var opener = o[pick.positionKey] || '';
    return {
      glyph: r.rune,
      name: name,
      meaning: r.elder_meaning,
      position: pick.position,
      reversed: pick.reversed,
      canReverse: pick.canReverse,
      reading: (opener ? opener + ' ' : '') + (o.body || ''),
      means: o.means || '',
      stone: STONES[e.slug] || '',
      noun: e.noun, att: e.att, tone: e.tone,
      /* The Norwegian poem is the shortest of the three, so it is the one that
         fits a card. The full set of three is on each rune's own page. */
      poem: r.norwegian_rune_poem || r.icelandic_rune_poem || r.old_english_rune_poem || '',
      poemEn: r.norwegian_poem_translation || r.icelandic_poem_translation
              || r.old_english_poem_translation || '',
      poemSrc: r.norwegian_rune_poem ? 'Norwegian rune poem, c. 13th century'
               : (r.icelandic_rune_poem ? 'Icelandic rune poem, c. 15th century'
                                        : 'Old English rune poem, c. 8th–10th century'),
      slug: e.slug + '-rune-meaning'
    };
  }

  /* ---------- yes / no ----------
     Only for the single draw. A rune is not a yes-or-no device; this is a modern
     convention and the page says so. It is computed from how that rune's stanzas
     actually run, bright reads as yes, hard as no, and a rune the poems genuinely
     split on returns neither. Reversal inverts a clean answer and leaves an
     unclear one unclear. */
  function yesNo(cast) {
    if (!cast || cast.picks.length !== 1) { return null; }
    var c = card(cast.picks[0]);
    if (c.tone === 'mixed') {
      return { answer: 'Neither', line: 'This rune is not a clean yes or no. The poems split on '
        + c.name + ', and a cast that lands on a contested stanza is telling you the question '
        + 'is not yet a yes-or-no question.' };
    }
    var yes = (c.tone === 'bright') !== !!c.reversed;
    return {
      answer: yes ? 'Yes' : 'No',
      line: (yes
        ? c.name + (c.reversed ? ' reversed' : '') + ' reads as favourable.'
        : c.name + (c.reversed ? ' reversed' : '') + ' reads as unfavourable.')
        + ' That reading comes from how its stanzas run in the rune poems, not from any '
        + 'medieval practice of asking runes yes-or-no questions. There is none on record.'
    };
  }

  /* ---------- topic detection ----------
     The visitor's own words, matched against plain keyword sets, so the closing
     line can point at what they actually asked about. No model, no inference
     beyond word matching: an unmatched question simply gets the neutral close. */
  var TOPICS = [
    ['money',        'money and what it costs',   /\b(money|rich|wealth|afford|debt|loan|salary|pay|paid|income|broke|savings?|invest|financ|budget|cash|price|expensive|bills?|rent|mortgage)\b/i],
    ['work',         'work',                      /\b(job|jobs|work|working|career|boss|promot|interview|hire[dr]?|hiring|fired|quit|resign|business|company|client|colleague|coworker|office|manager)\b/i],
    ['love',         'a relationship',            /\b(love|loves?|loving|relationship|partner|boyfriend|girlfriend|husband|wife|spouse|marry|marriage|married|divorce|dating|date|ex|crush|romanti|breakup|break up)\b/i],
    ['health',       'health',                    /\b(health|healthy|ill|illness|sick|sickness|disease|pain|injur|surgery|doctor|hospital|diagnos|recover|heal|tired|exhaust|sleep|anxiet|depress|therapy)\b/i],
    ['family',       'family',                    /\b(family|mother|mom|mum|father|dad|parents?|son|daughter|child|children|kids?|brother|sister|sibling|grand(ma|pa|mother|father)|in-laws?)\b/i],
    ['home',         'where you live',            /\b(home|house|flat|apartment|move|moving|relocat|neighbou?r|landlord|buy a (house|flat)|renovat)\b/i],
    ['decision',     'a decision',                /\b(should i|decide|decision|choose|choice|choosing|option|whether|or not|which one|take the|accept the|say yes|say no)\b/i],
    ['conflict',     'a conflict',                /\b(fight|fighting|argu|conflict|dispute|enemy|enemies|betray|angry|anger|revenge|forgive|apolog|falling out|fell out)\b/i],
    ['direction',    'where you are going',       /\b(purpose|meaning|direction|lost|stuck|path|calling|what am i|who am i|future|destiny|fate|next step|change my life)\b/i],
    ['travel',       'a journey',                 /\b(travel|trip|journey|abroad|emigrat|immigrat|visa|country|leave the country|go back|return home)\b/i],
    ['study',        'study',                     /\b(study|studying|school|university|college|exam|exams|degree|course|learn|learning|thesis|student)\b/i],
    ['friendship',   'a friendship',              /\b(friend|friends|friendship|mate|mates|social|lonely|loneliness|trust (him|her|them))\b/i],
    ['loss',         'a loss',                    /\b(died|die|death|dead|grief|grieving|funeral|lost (my|him|her|them)|passed away|mourn|miss (him|her|them))\b/i],
    ['creative',     'something you are making',  /\b(write|writing|book|album|music|song|art|paint|design|build|project|launch|start(ing)? (a|my)|creat)\b/i],
    ['legal',        'a legal matter',            /\b(court|lawyer|legal|contract|sue|sued|lawsuit|case|police|claim|settlement)\b/i],
    ['self',         'yourself',                  /\b(myself|my life|change myself|confiden|self[- ]?worth|self[- ]?esteem|identity|who i am|become)\b/i]
  ];
  /* Scored, not first-match. "Is my mother going to recover" hits both family and
     health; first-match would return whichever topic happened to be listed first,
     so every topic is counted and the strongest wins, ties going to list order. */
  function detectTopic(q) {
    if (!q) { return null; }
    var best = null, bestN = 0;
    for (var i = 0; i < TOPICS.length; i++) {
      var re = new RegExp(TOPICS[i][2].source, 'gi');
      var hits = (String(q).match(re) || []).length;
      if (hits > bestN) { bestN = hits; best = TOPICS[i]; }
    }
    return best ? { id: best[0], label: best[1] } : null;
  }

  var AETT = { 1: 'Freyr and Freyja’s ætt', 2: 'Heimdall’s ætt', 3: 'Týr’s ætt' };

  /* ---------- synthesis ----------
     There is no thread sentence here on purpose. Once every card carries a
     position-aware paragraph, a line restating "it grew out of X, it stands in Y"
     only repeats what the reader just read. What survives is the part no
     competitor has: two counted observations, reported only when they are
     actually unlikely, and a close that points at the reader's own question.

     "Two of three runes share an ætt" happens in about 70% of casts, so saying it
     would be flattery dressed as insight. The whole cast sharing one lands near
     8%; the whole cast reading bright or hard, near 6%. Those are worth printing. */
  function synthesize(cast, question) {
    if (!DATA || !cast || cast.picks.length < 2) { return null; }
    var cards = cast.picks.map(card);
    var q = String(question || '').trim().slice(0, 140);
    var topic = detectTopic(q);
    var notes = [];

    /* 1. Ætt cluster. The Elder Futhark splits into three groups of eight and
          that division is part of the runic tradition itself, cipher runes count
          against it, so a cluster is a real pattern, not one we invented. */
    var by = {}, top = null, topN = 0;
    cards.forEach(function (c) { by[c.att] = (by[c.att] || 0) + 1; });
    Object.keys(by).forEach(function (k) { if (by[k] > topN) { topN = by[k]; top = k; } });
    if ((cards.length === 3 && topN === 3) || (cards.length === 5 && topN >= 4)) {
      notes.push((topN === cards.length ? 'All ' + cards.length : 'Four') + ' of your runes fall in '
        + AETT[top] + '. The Elder Futhark splits into three groups of eight, and that division is '
        + 'old enough that cipher runes count against it. A cast landing this way happens in roughly '
        + 'one in twelve.');
    }

    /* 2. Reversals, at the extremes only. Runes that cannot turn are excluded, or
          "none reversed" would be reported on casts where reversal was never possible. */
    if (cast.reversed) {
      var canRev = cast.picks.filter(function (p) { return p.canReverse; });
      var didRev = canRev.filter(function (p) { return p.reversed; });
      if (canRev.length >= 2 && didRev.length === canRev.length) {
        notes.push('Every rune here that could come up reversed did. Reversal is a modern '
          + 'convention rather than a Norse one, and you can switch it off above.');
      } else if (canRev.length >= 2 && didRev.length === 0) {
        notes.push('Nothing came up reversed. Every stave here is standing as it was cut.');
      }
    }

    /* 3. Weight of the stanzas. Our tagging, and labelled as ours. */
    var need = cards.length === 5 ? 4 : cards.length;
    var hard = cards.filter(function (c) { return c.tone === 'hard'; }).length;
    var bright = cards.filter(function (c) { return c.tone === 'bright'; }).length;
    if (hard >= need) {
      notes.push(hard + ' of your ' + cards.length + ' runes sit on stanzas that read as hardship: '
        + 'need, ice, hail, the thorn. That is our reading of how those verses run, not a verdict '
        + 'on your question.');
    } else if (bright >= need) {
      notes.push(bright + ' of your ' + cards.length + ' runes sit on stanzas that read as plenty: '
        + 'the gift, the harvest, the sun. That is our reading of how those verses run, not a '
        + 'promise about your question.');
    }

    /* The close. Every branch names the actual nouns first, because a closing line
       that only talks about how to read runes says nothing about the cast on screen. */
    var nouns = cards.map(function (c) { return c.noun; });
    var listed = nouns.length > 1
      ? nouns.slice(0, -1).join(', ') + ' and ' + nouns[nouns.length - 1]
      : nouns[0];
    var close;
    if (topic) {
      close = 'You asked about ' + topic.label + '. The cast gives you ' + listed
        + '. Read each one as a question about ' + topic.label + ', not as an answer to it.';
    } else if (q) {
      close = 'The cast gives you ' + listed + '. Hold each one against “' + q
        + '” and see which of them fits. Deciding that is the part the runes cannot do for you.';
    } else {
      close = 'The cast gives you ' + listed + '. Nothing in the draw says what those apply to, '
        + 'so type your question above and cast again if you want a sharper reading.';
    }
    return { question: q, topic: topic, notes: notes.slice(0, 2), close: close };
  }

  /* ---------- share card ---------- */
  var FORMATS = [
    { id: 'story',     label: 'Instagram Story', w: 1080, h: 1920, note: 'Stories and Reels' },
    { id: 'post',      label: 'Square Post',     w: 1080, h: 1080, note: 'Instagram, Facebook' },
    { id: 'portrait',  label: 'Portrait Post',   w: 1080, h: 1350, note: 'Instagram feed' },
    { id: 'og',        label: 'Link Preview',    w: 1200, h: 630,  note: 'X, LinkedIn, WhatsApp' },
    { id: 'wallpaper', label: 'Phone Wallpaper', w: 1170, h: 2532, note: 'iPhone / Android' }
  ];

  var BG = null, STONE_IMGS = {};
  function setBg(img) { BG = img; }
  function setStoneImage(slug, img) { STONE_IMGS[slug] = img; }

  function drawCover(ctx, img, W, H) {
    if (!img || !img.complete || !img.naturalWidth) {
      var g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, '#171310'); g.addColorStop(1, '#0b0908');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H); return;
    }
    var s = Math.max(W / img.naturalWidth, H / img.naturalHeight);
    var dw = img.naturalWidth * s, dh = img.naturalHeight * s;
    ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
  }

  function wrap(ctx, text, maxW) {
    var words = String(text || '').split(/\s+/), lines = [], line = '';
    for (var i = 0; i < words.length; i++) {
      var test = line ? line + ' ' + words[i] : words[i];
      if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = words[i]; }
      else { line = test; }
    }
    if (line) { lines.push(line); }
    return lines;
  }

  /* Canvas has no letter-spacing on older Safari, so the eyebrow is tracked by
     hand, one glyph at a time. */
  function trackText(ctx, text, cx, y, sp) {
    var i, w = ctx.measureText(text).width + sp * (text.length - 1);
    var x = cx - w / 2, prev = ctx.textAlign;
    ctx.textAlign = 'left';
    for (i = 0; i < text.length; i++) {
      ctx.fillText(text[i], x, y);
      x += ctx.measureText(text[i]).width + sp;
    }
    ctx.textAlign = prev;
  }

  /* Wraps to a line budget, but cuts at a sentence boundary rather than mid
     clause. A card that ends on "the defence and the threat are not lined"
     reads as a rendering fault, not as an edit. */
  function fitLines(ctx, text, maxW, maxLines) {
    var lines = wrap(ctx, text, maxW);
    if (lines.length <= maxLines) { return lines; }
    var parts = String(text || '').match(/[^.!?]+[.!?]+[\s]*/g) || [];
    var acc = '';
    for (var i = 0; i < parts.length; i++) {
      if (wrap(ctx, (acc + parts[i]).trim(), maxW).length > maxLines) { break; }
      acc += parts[i];
    }
    acc = acc.trim();
    return acc ? wrap(ctx, acc, maxW) : lines.slice(0, maxLines);
  }

  /* Draws the actual stone photograph when it has loaded, and falls back to the
     runic font otherwise, so a card is never blank. */
  function drawStone(ctx, c, x, y, size) {
    var img = STONE_IMGS[c.slug.replace('-rune-meaning', '')];
    ctx.save();
    ctx.translate(x, y);
    if (c.reversed) { ctx.rotate(Math.PI); }
    if (img && img.complete && img.naturalWidth) {
      ctx.drawImage(img, -size / 2, -size / 2, size, size);
    } else {
      ctx.font = Math.round(size * 0.62) + 'px "Noto Sans Runic","Segoe UI Historic",serif';
      ctx.fillStyle = '#F1E7D2'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(c.glyph, 0, 0);
    }
    ctx.restore();
    ctx.textBaseline = 'alphabetic'; ctx.textAlign = 'center';
  }

  /* The card renders the WHOLE cast, not its first stave: on a three- or
     five-rune spread the shareable object is the spread, and a card showing one
     of the five is the reading with four fifths cut off. */
  function renderCard(cast, fmt, cb) {
    var W = fmt.w, H = fmt.h;
    var cards = cast.picks.map(card);
    var cv = document.createElement('canvas');
    cv.width = W; cv.height = H;
    var ctx = cv.getContext('2d');

    drawCover(ctx, BG, W, H);
    /* These alphas were written as rgba(12,9,7.62). CSS Color 4 reads a three
       component rgba() as opaque, so the scrim was not a scrim: it painted flat
       black over the texture and every card came out on a dead ground. */
    ctx.fillStyle = 'rgba(12,9,7,0.72)';
    ctx.fillRect(0, 0, W, H);

    /* a slow warm pool where the stones sit, so the ground reads as lit rather
       than as a fill colour */
    var glow = ctx.createRadialGradient(W / 2, H * 0.36, 0, W / 2, H * 0.36, W * 0.78);
    glow.addColorStop(0, 'rgba(216,188,142,0.11)');
    glow.addColorStop(1, 'rgba(216,188,142,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);

    /* The frame is inset from the SHORT side. Deriving it from the width alone
       cost a 1200x630 link preview 192px of its 630, which is where the format
       ran out of room for the reading. */
    var pad = Math.round(Math.min(W, H) * 0.08);
    ctx.strokeStyle = 'rgba(216,188,142,0.30)';
    ctx.lineWidth = Math.max(2, Math.round(W / 540));
    ctx.strokeRect(pad * 0.6, pad * 0.6, W - pad * 1.2, H - pad * 1.2);

    var cx = W / 2, maxW = W - pad * 2;
    var U = Math.min(W, H * 1.2);   /* type scale: the short side governs */
    ctx.textAlign = 'center';

    var fHead = Math.round(U * 0.026);
    ctx.font = '600 ' + fHead + 'px Georgia,serif';
    ctx.fillStyle = 'rgba(216,188,142,0.92)';
    trackText(ctx, 'FREE RUNE READING', cx, pad * 1.35, fHead * 0.22);

    var box = { top: pad * 1.35 + fHead * 1.5, bottom: H - pad * 1.7 };
    if (cards.length === 1) { drawSingle(ctx, cast, cards[0], W, H, pad, cx, maxW, U, box); }
    else { drawSpread(ctx, cast, cards, W, H, pad, cx, maxW, U, box); }

    ctx.textAlign = 'center';
    ctx.font = '600 ' + Math.round(U * 0.022) + 'px Georgia,serif';
    ctx.fillStyle = 'rgba(216,188,142,0.75)';
    ctx.fillText('CAST YOURS FREE AT VIKINGSBRAND.CO', cx, H - pad * 0.8);

    cv.toBlob(function (b) { if (b) { cb(b); } }, 'image/png');
  }

  /* Both card layouts measure their block, fit it to the frame, and centre what
     is left. The old version pinned the stones at H * 0.30 and sized every font
     off W, which is only correct on a square: a 9:16 story left its bottom third
     empty, and a 1200x630 link preview ran its reading straight off the card. */
  function drawSingle(ctx, cast, c, W, H, pad, cx, maxW, U, box) {
    var yn = yesNo(cast);
    var avail = box.bottom - box.top;

    function build(k, readBudget, wantPoem) {
      var f = {
        size: Math.round(Math.min(W * 0.42, H * 0.36) * k),
        name: Math.round(U * 0.075 * k),
        yn: Math.round(U * 0.042 * k),
        body: Math.round(U * 0.034 * k), lhBody: Math.round(U * 0.046 * k),
        poem: Math.round(U * 0.028 * k), lhPoem: Math.round(U * 0.038 * k),
        gap: Math.round(U * 0.05 * k)
      };
      ctx.font = f.body + 'px Georgia,serif';
      var readLines = fitLines(ctx, c.reading, maxW, readBudget);
      ctx.font = 'italic ' + f.poem + 'px Georgia,serif';
      var poemLines = (wantPoem && c.poemEn) ? fitLines(ctx, '“' + c.poemEn + '”', maxW, 3) : [];
      var total = Math.round(f.size * 1.10) + f.gap + (yn ? f.gap : 0)
        + readLines.length * f.lhBody
        + (poemLines.length ? Math.round(U * 0.022 * k) + poemLines.length * f.lhPoem : 0);
      return { f: f, readLines: readLines, poemLines: poemLines, total: total, k: k };
    }

    /* Shed text before shrinking type. A card that drops the rune poem and a
       sentence of the reading still reads; one scaled to half size does not. */
    var plans = [[6, true], [6, false], [5, false], [4, false], [3, false]], L;
    for (var pi = 0; pi < plans.length; pi++) {
      L = build(1, plans[pi][0], plans[pi][1]);
      if (L.total <= avail) { break; }
    }
    if (L.total > avail) { L = build(Math.max(0.78, avail / L.total), 3, false); }
    var f = L.f;
    var start = box.top + Math.max(0, (avail - L.total) / 2);

    drawStone(ctx, c, cx, start + f.size / 2, f.size);
    var y = start + f.size * 1.10;

    ctx.font = '700 ' + f.name + 'px Cinzel,Georgia,serif';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(c.name + (c.reversed ? ' · reversed' : ''), cx, y);
    y += f.gap;

    if (yn) {
      ctx.font = '700 ' + f.yn + 'px Cinzel,Georgia,serif';
      ctx.fillStyle = '#D8BC8E';
      ctx.fillText(yn.answer.toUpperCase(), cx, y);
      y += f.gap;
    }

    ctx.font = f.body + 'px Georgia,serif';
    ctx.fillStyle = '#EDE4CE';
    L.readLines.forEach(function (l) { ctx.fillText(l, cx, y); y += f.lhBody; });

    if (L.poemLines.length) {
      y += Math.round(U * 0.022 * L.k);
      ctx.font = 'italic ' + f.poem + 'px Georgia,serif';
      ctx.fillStyle = 'rgba(216,188,142,0.85)';
      L.poemLines.forEach(function (l) { ctx.fillText(l, cx, y); y += f.lhPoem; });
    }
  }

  function drawSpread(ctx, cast, cards, W, H, pad, cx, maxW, U, box) {
    var n = cards.length, col = maxW / n;
    var anyRev = cards.some(function (c) { return c.reversed; });
    var s = synthesize(cast, cast.question);
    var avail = box.bottom - box.top;
    var tall = H / W >= 1.45;

    function build(k, detBudget, closeBudget, wantNote) {
      var f = {
        /* Stones shrink to whichever runs out first, the column or the frame
           height. Five staves on a link preview is the tight case. */
        size: Math.round(Math.min(col * 0.88, H * 0.24) * k),
        title: Math.round(U * 0.024 * k),
        name: Math.round(Math.min(col * 0.16, U * 0.032) * k),
        rev: Math.round(Math.min(col * 0.10, U * 0.019) * k),
        body: Math.round(U * 0.032 * k), lhBody: Math.round(U * 0.044 * k),
        note: Math.round(U * 0.026 * k), lhNote: Math.round(U * 0.036 * k),
        pos: Math.round(U * 0.021 * k),
        det: Math.round(U * 0.029 * k), lhDet: Math.round(U * 0.040 * k),
        revGap: Math.round(U * 0.028 * k)
      };
      ctx.font = f.body + 'px Georgia,serif';
      var closeLines = s ? fitLines(ctx, s.close, maxW, closeBudget) : [];
      ctx.font = 'italic ' + f.note + 'px Georgia,serif';
      var noteLines = (s && s.notes.length && wantNote && tall)
        ? fitLines(ctx, s.notes[0], maxW, 3) : [];
      var details = [];
      if (detBudget) {
        ctx.font = f.det + 'px Georgia,serif';
        details = cards.map(function (c) {
          return { label: c.position, lines: fitLines(ctx, c.reading, maxW * 0.94, detBudget) };
        });
      }
      var stoneH = Math.round(f.size * 1.12) + Math.round(f.name * 0.35)
        + (anyRev ? f.revGap : 0);
      var total = f.title + Math.round(U * 0.05 * k) + stoneH
        + Math.round(U * 0.04 * k) + Math.round(U * 0.05 * k)
        + closeLines.length * f.lhBody
        + (noteLines.length ? Math.round(U * 0.02 * k) + noteLines.length * f.lhNote : 0)
        + details.reduce(function (t, d) {
            return t + Math.round(U * 0.045 * k) + Math.round(U * 0.03 * k)
                   + d.lines.length * f.lhDet;
          }, 0);
      return { f: f, closeLines: closeLines, noteLines: noteLines, details: details,
               stoneH: stoneH, total: total, k: k };
    }

    /* A tall card spends its extra room on the per-position readings, which is
       the part of the cast the old layout dropped. Everything past that is shed
       in order of how little it costs the reader: fewer lines of the readings,
       then no readings, then the footnote, then whole sentences off the close.
       Type size is the last thing to give, and never by more than a fifth. */
    var plans = [];
    if (tall) {
      [5, 4, 3, 2].forEach(function (d) { plans.push([d, 6, true]); });
    }
    plans.push([0, 6, true], [0, 5, false], [0, 4, false], [0, 3, false]);
    var L = null;
    for (var i = 0; i < plans.length; i++) {
      L = build(1, plans[i][0], plans[i][1], plans[i][2]);
      if (L.total <= avail) { break; }
    }
    if (L.total > avail) { L = build(Math.max(0.78, avail / L.total), 0, 3, false); }

    var f = L.f, k = L.k;
    var y = box.top + Math.max(0, (avail - L.total) / 2) + f.title;

    ctx.textAlign = 'center';
    ctx.font = '600 ' + f.title + 'px Georgia,serif';
    ctx.fillStyle = '#D8BC8E';
    ctx.fillText(MODES[cast.mode].cardTitle.toUpperCase(), cx, y);
    y += Math.round(U * 0.05 * k);

    var rowY = y + f.size / 2;
    cards.forEach(function (c, i2) {
      var x = pad + col * i2 + col / 2;
      drawStone(ctx, c, x, rowY, f.size);
      var ny = rowY + f.size * 0.62;
      ctx.font = '700 ' + f.name + 'px Cinzel,Georgia,serif';
      ctx.fillStyle = '#FFFFFF';
      ctx.fillText(c.name, x, ny);
      if (c.reversed) {
        ctx.font = '600 ' + f.rev + 'px Georgia,serif';
        ctx.fillStyle = '#E0B878';
        ctx.fillText('reversed', x, ny + f.revGap);
      }
    });
    y += L.stoneH + Math.round(U * 0.04 * k);

    ctx.strokeStyle = 'rgba(216,188,142,0.35)';
    ctx.beginPath();
    ctx.moveTo(cx - W * 0.06, y); ctx.lineTo(cx + W * 0.06, y); ctx.stroke();
    y += Math.round(U * 0.05 * k);

    ctx.font = f.body + 'px Georgia,serif';
    ctx.fillStyle = '#EDE4CE';
    L.closeLines.forEach(function (l) { ctx.fillText(l, cx, y); y += f.lhBody; });

    if (L.noteLines.length) {
      y += Math.round(U * 0.02 * k);
      ctx.font = 'italic ' + f.note + 'px Georgia,serif';
      ctx.fillStyle = 'rgba(216,188,142,0.85)';
      L.noteLines.forEach(function (l) { ctx.fillText(l, cx, y); y += f.lhNote; });
    }

    L.details.forEach(function (d) {
      y += Math.round(U * 0.045 * k);
      ctx.font = '700 ' + f.pos + 'px Cinzel,Georgia,serif';
      ctx.fillStyle = '#D8BC8E';
      ctx.fillText(d.label.length > 18 ? d.label : d.label.toUpperCase(), cx, y);
      y += Math.round(U * 0.03 * k);
      ctx.font = f.det + 'px Georgia,serif';
      ctx.fillStyle = '#E6DCC6';
      d.lines.forEach(function (l) { ctx.fillText(l, cx, y); y += f.lhDet; });
    });
  }

  function fileSlug(cast) {
    if (cast.picks.length === 1) {
      var c = card(cast.picks[0]);
      return 'rune-' + c.name.toLowerCase().replace(/[^a-z]/g, '')
             + (c.reversed ? '-reversed' : '');
    }
    return 'rune-cast-' + cast.mode + '-' + cast.seed;
  }
  function castLabel(cast) {
    return cast.picks.map(function (p) {
      var c = card(p);
      return c.name + (c.reversed ? ' reversed' : '');
    }).join(', ');
  }

  function downloadCard(cast, fmt) {
    track('download', { mode: cast.mode, count: cast.picks.length, format: fmt.id });
    renderCard(cast, fmt, function (blob) {
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url; a.download = fileSlug(cast) + '-' + fmt.id + '.png';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
    });
  }

  /* Tagged permalink, so visits arriving from a shared card can be told apart
     from the rest. Existing utm_* are stripped first so re-shares do not compound. */
  function shareUrl(medium) {
    try {
      var u = new URL(location.href);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
        .forEach(function (k) { u.searchParams.delete(k); });
      u.searchParams.set('utm_source', 'runecast');
      u.searchParams.set('utm_medium', medium);
      u.searchParams.set('utm_campaign', 'rune-reading');
      return u.toString();
    } catch (e) { return location.href; }
  }

  function shareCard(cast, fmt) {
    track('share', { mode: cast.mode, count: cast.picks.length, format: fmt.id });
    renderCard(cast, fmt, function (blob) {
      var file = new File([blob], fileSlug(cast) + '.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        navigator.share({
          files: [file],
          title: MODES[cast.mode].cardTitle,
          /* The link rides in `text`: targets that accept a file use `text` as the
             caption and are free to drop `url` entirely. */
          text: 'I drew ' + castLabel(cast) + '. Cast your own: ' + shareUrl('social')
        }).catch(function () {});
      } else {
        downloadCard(cast, fmt);
      }
    });
  }

  /* Usage tracking, matching vb-runes.js: fire at whatever analytics is already
     on the page and no-op when there is none. The CustomEvent is kept for any
     future listener, but on its own it reached nothing, which is what this tool
     was doing before.

     NEVER send the question. The page promises it is not transmitted and that
     promise is load-bearing. `has_question` is a boolean about whether the field
     was used at all, which is what we actually need to know. */
  function track(action, d) {
    try {
      d = d || {}; d.action = action;
      var e = { tool: 'rune-reading', vb_action: action };
      ['mode', 'count', 'format', 'reversed', 'has_question'].forEach(function (k) {
        if (d[k] !== undefined) { e[k] = d[k]; }
      });
      var name = 'runecast_' + action;
      if (window.gtag) { window.gtag('event', name, e); }
      if (window.dataLayer && window.dataLayer.push) {
        var g = { event: name };
        for (var k in e) { g[k] = e[k]; }
        window.dataLayer.push(g);
      }
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(name, e);
      }
      document.dispatchEvent(new CustomEvent('vb:rune-cast', { detail: d }));
    } catch (e2) {}
  }

  window.VBRuneCast = {
    setData: function (d) { DATA = d; },
    setReadings: function (d) { READ = d; },
    setStones: function (m) { STONES = m || {}; },
    getData: function () { return DATA; },
    ready: function () { return !!(DATA && READ); },
    MODES: MODES, FORMATS: FORMATS, RUNES: RUNES, TOPICS: TOPICS,
    draw: draw, card: card, synthesize: synthesize, yesNo: yesNo,
    detectTopic: detectTopic,
    newSeed: newSeed, daySeed: daySeed, fnv1a: fnv1a,
    setBg: setBg, setStoneImage: setStoneImage, renderCard: renderCard,
    downloadCard: downloadCard, shareCard: shareCard,
    shareUrl: shareUrl, track: track
  };
})();
